import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider } from './contexts/AuthContext';
import { PermissionProvider } from './contexts/PermissionContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { MobileDeviceProvider } from './contexts/MobileDeviceContext';
import ProtectedRoute from './components/ProtectedRoute';
import RoleProtectedRoute from './components/RoleProtectedRoute';
import PermissionRoute from './components/PermissionRoute';
import DashboardResolver from './components/DashboardResolver';
import Shell from './components/Shell';
import Login from './pages/Login';
import SetPassword from './pages/SetPassword';
import NurseDashboard from './pages/NurseDashboard';
import AdminDashboard from './pages/AdminDashboard';
import CaregiverDashboard from './pages/CaregiverDashboard';
import PhysiotherapistDashboard from './pages/PhysiotherapistDashboard';
import KitchenDashboard from './pages/KitchenDashboard';
import Residents from './pages/Residents';
import InactiveResidents from './pages/InactiveResidents';
import AddResident from './pages/AddResident';
import ResidentProfile from './pages/ResidentProfile';
import ResidentHealth from './pages/ResidentHealth';
import ResidentSchedule from './pages/ResidentSchedule';
import ResidentNutrition from './pages/ResidentNutrition';
import ResidentFamily from './pages/ResidentFamily';
import ResidentFinances from './pages/ResidentFinances';
import Settings from './pages/Settings';
import Analytics from './pages/Analytics';
import Users from './pages/Users';
import Employees from './pages/Employees';
import InactiveEmployees from './pages/InactiveEmployees';
import EmployeeProfile from './pages/EmployeeProfile';
import Rooms from './pages/Rooms';
import Reports from './pages/Reports';
import Profile from './pages/Profile';
import ShiftManagement from './pages/ShiftManagement';
import Tasks from './pages/Tasks';
import CaregiverTasksPage from './pages/CaregiverTasksPage';
import PhysiotherapistTasksPage from './pages/PhysiotherapistTasksPage';
import NurseTasksPage from './pages/NurseTasksPage';
import Meals from './pages/Meals';
import Menu from './pages/Menu';
import KitchenAdmin from './pages/KitchenAdmin';
import HomePanel from './pages/HomePanel';
import Help from './pages/Help';
import Blog from './pages/Blog';
import Events from './pages/Events';
import Forbidden from './pages/Forbidden';
import './App.css';

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <PermissionProvider>
        <NotificationProvider>
        {/* Router mora biti iznad MobileDeviceProvider-a (i svakog budućeg
            provajdera koji koristi react-router hook-ove): provider dobija
            `navigate` preko useNavigate() i prosleđuje ga
            notificationNavigationService-u. Obrnut redosled ruši ceo React
            root sa "useNavigate() may be used only in the context of a
            <Router> component" (beli ekran). */}
        <Router>
        <MobileDeviceProvider>
          <Routes>
            {/* Public Route - Login */}
            <Route path="/login" element={<Login />} />
            <Route path="/set-password" element={<SetPassword />} />
            
            {/* Protected Routes - Wrapped in Shell */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Shell />
                </ProtectedRoute>
              }
            >
              {/* Redirect root to appropriate dashboard based on role */}
              <Route index element={<DashboardResolver />} />
              <Route path="dashboard" element={<DashboardResolver />} />
              
              {/* Role-protected dashboards */}
              <Route 
                path="nurse-dashboard" 
                element={
                  <RoleProtectedRoute allowedRoles={['nurse', 'caretaker']}>
                    <NurseDashboard />
                  </RoleProtectedRoute>
                } 
              />
              <Route 
                path="admin-dashboard" 
                element={
                  <RoleProtectedRoute allowedRoles={['administrator']}>
                    <AdminDashboard />
                  </RoleProtectedRoute>
                } 
              />
              <Route 
                path="caregiver-dashboard" 
                element={
                  <RoleProtectedRoute allowedRoles={['caregiver']}>
                    <CaregiverDashboard />
                  </RoleProtectedRoute>
                } 
              />
              <Route 
                path="physiotherapist-dashboard" 
                element={
                  <RoleProtectedRoute allowedRoles={['physiotherapist']}>
                    <PhysiotherapistDashboard />
                  </RoleProtectedRoute>
                } 
              />
              <Route 
                path="kitchen-dashboard" 
                element={
                  <RoleProtectedRoute allowedRoles={['kitchen']}>
                    <KitchenDashboard />
                  </RoleProtectedRoute>
                } 
              />
              
              <Route path="residents" element={<PermissionRoute path="/residents"><Residents /></PermissionRoute>} />
              {/* „Grupe" su sada tab stranice „Organizacija" (Settings) - ruta
                  je zadržana da stari linkovi i dalje rade. */}
              <Route path="residents/groups" element={<PermissionRoute path="/residents/groups"><Settings /></PermissionRoute>} />
              <Route path="residents/inactive" element={<PermissionRoute path="/residents"><InactiveResidents /></PermissionRoute>} />
              <Route path="residents/add" element={<PermissionRoute path="/residents"><AddResident /></PermissionRoute>} />
              <Route path="residents/:id" element={<PermissionRoute path="/residents"><ResidentProfile /></PermissionRoute>} />
              <Route path="residents/:id/health" element={<PermissionRoute path="/residents"><ResidentHealth /></PermissionRoute>} />
              <Route path="residents/:id/schedule" element={<PermissionRoute path="/residents"><ResidentSchedule /></PermissionRoute>} />
              <Route path="residents/:id/nutrition" element={<PermissionRoute path="/residents"><ResidentNutrition /></PermissionRoute>} />
              <Route path="residents/:id/family" element={<PermissionRoute path="/residents"><ResidentFamily /></PermissionRoute>} />
              <Route path="residents/:id/finances" element={<PermissionRoute path="/residents"><ResidentFinances /></PermissionRoute>} />
              <Route path="analytics" element={<Analytics />} />
              <Route path="users" element={<Users />} />
              <Route path="employees" element={<PermissionRoute path="/employees"><Employees /></PermissionRoute>} />
              <Route path="employees/inactive" element={<PermissionRoute path="/employees"><InactiveEmployees /></PermissionRoute>} />
              <Route path="employees/:id" element={<PermissionRoute path="/employees"><EmployeeProfile /></PermissionRoute>} />
              <Route path="rooms" element={<PermissionRoute path="/rooms"><Rooms /></PermissionRoute>} />
              <Route path="reports" element={<Reports />} />
              <Route path="shift-management" element={<PermissionRoute path="/shift-management"><ShiftManagement /></PermissionRoute>} />
              <Route path="tasks" element={<PermissionRoute path="/tasks"><Tasks /></PermissionRoute>} />
              <Route path="tasks/:taskId" element={<PermissionRoute path="/tasks"><Tasks /></PermissionRoute>} />
              <Route
                path="caregiver-tasks"
                element={
                  <RoleProtectedRoute allowedRoles={['caregiver']}>
                    <CaregiverTasksPage />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="caregiver-tasks/:taskId"
                element={
                  <RoleProtectedRoute allowedRoles={['caregiver']}>
                    <CaregiverTasksPage />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="physiotherapist-tasks"
                element={
                  <RoleProtectedRoute allowedRoles={['physiotherapist']}>
                    <PhysiotherapistTasksPage />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="physiotherapist-tasks/:taskId"
                element={
                  <RoleProtectedRoute allowedRoles={['physiotherapist']}>
                    <PhysiotherapistTasksPage />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="nurse-tasks"
                element={
                  <RoleProtectedRoute allowedRoles={['nurse', 'caretaker']}>
                    <NurseTasksPage />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="nurse-tasks/:taskId"
                element={
                  <RoleProtectedRoute allowedRoles={['nurse', 'caretaker']}>
                    <NurseTasksPage />
                  </RoleProtectedRoute>
                }
              />
              {/* Pregled obroka ima svako kome je dodeljena dozvola za modul
                  (meals.manage / menus.manage); dodavanje i brisanje obroka
                  zahteva `meals.manage` unutar same stranice. */}
              <Route
                path="meals"
                element={
                  <PermissionRoute path="/meals"><Meals /></PermissionRoute>
                }
              />
              <Route path="jelovnik" element={<PermissionRoute path="/jelovnik"><Menu /></PermissionRoute>} />
              {/* Katalog aktivnosti je tab stranice „Organizacija" (Settings);
                  pristup ide po dozvoli (kao i stavka u meniju). Sam katalog
                  backend menja samo administratorima, pa su dugmad unutar
                  stranice i dalje vezana za rolu. */}
              <Route
                path="activities"
                element={
                  <PermissionRoute path="/activities"><Settings /></PermissionRoute>
                }
              />
              <Route
                path="kuhinja-administracija"
                element={
                  <PermissionRoute path="/kuhinja-administracija"><KitchenAdmin /></PermissionRoute>
                }
              />
              <Route
                path="panel-doma"
                element={
                  <RoleProtectedRoute allowedRoles={['administrator']}>
                    <HomePanel />
                  </RoleProtectedRoute>
                }
              />
              <Route path="profile" element={<Profile />} />
              <Route path="blog" element={<Blog />} />
              <Route path="events" element={<Events />} />
              <Route path="help" element={<Help />} />
              <Route path="settings" element={<Settings />} />
              <Route path="403" element={<Forbidden />} />
            </Route>

            {/* Catch all - redirect to root which resolves to appropriate dashboard */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </MobileDeviceProvider>
        </Router>
        </NotificationProvider>
        </PermissionProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
